import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environments';

export interface AuthApiResponse<T = unknown> {
  apiResponse: {
    statusCode: number;
    data: T | null;
    message: string;
  };
  entityResponse: {
    sesssionToken: string;
    sessionToken?: string;
    memberCode: string;
    memberName: string;
    memberPhone: string;
    customerTypeid: string;
    customerTypeName: string;
    customerSubTypeid: string;
    customerSubTypeName: string;
    memberPhoto: string;
    memberStatus: boolean | null;
    memberCardStatus: number | null;
    memberCardStatusName: string | null;
    dateOfJoin: string | null;
    age: number | null;
    isFirstTimeLogin?: boolean | null;
  } | null;
}

export const MEMBER_PROFILE_STORAGE_KEYS = [
  'memberCode',
  'memberName',
  'memberPhone',
  'customerTypeName',
  'customerSubTypeName',
  'memberPhoto',
  'dateOfJoin',
  'age',
] as const;

type MemberProfileStorageKey = (typeof MEMBER_PROFILE_STORAGE_KEYS)[number];

export type MemberProfileForUi = { [K in MemberProfileStorageKey]?: string | null };

let memoryProfile: MemberProfileForUi = {};

export function readMemberProfileFromStorage(): MemberProfileForUi {
  if (typeof window === 'undefined') {
    return {};
  }
  
  if (Object.keys(memoryProfile).length > 0) {
    return memoryProfile;
  }
  
  const state = history.state as { profile?: Record<string, unknown> };
  if (state?.profile) {
    persistMemberProfileFromLooseRecord(state.profile);
    return memoryProfile;
  }
  
  const storedCode = localStorage.getItem('memberCode');
  if (storedCode) {
    memoryProfile['memberCode'] = storedCode;
  }

  const storedName = localStorage.getItem('memberName');
  if (storedName) {
    memoryProfile['memberName'] = storedName;
  }

  const storedPhone = localStorage.getItem('memberPhone');
  if (storedPhone) {
    memoryProfile['memberPhone'] = storedPhone;
  }

  const storedCustomerSubTypeName = localStorage.getItem('customerSubTypeName');
  if (storedCustomerSubTypeName) {
    memoryProfile['customerSubTypeName'] = storedCustomerSubTypeName;
  }

  const storedMemberPhoto = localStorage.getItem('memberPhoto');
  if (storedMemberPhoto) {
    memoryProfile['memberPhoto'] = storedMemberPhoto;
  }

  const storedDateOfJoin = localStorage.getItem('dateOfJoin');
  if (storedDateOfJoin) {
    memoryProfile['dateOfJoin'] = storedDateOfJoin;
  }

  const storedAge = localStorage.getItem('age');
  if (storedAge) {
    memoryProfile['age'] = storedAge;
  }

  if (Object.keys(memoryProfile).length > 0) {
    return memoryProfile;
  }
  
  return {};
}

export function pickStr(raw: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (v === null || v === undefined) continue;
    const s = `${v}`.trim();
    if (s && s.toLowerCase() !== 'null') return s;
  }
  return undefined;
}

/** localStorage key + JSON keys (camel / Pascal / snake / directory aliases). */
const PROFILE_FIELD_SOURCES: readonly [string, readonly string[]][] = [
  ['memberCode', ['memberCode', 'MemberCode', 'code', 'membershipCode']],
  ['memberName', ['memberName', 'MemberName', 'name', 'fullName']],
  ['memberPhone', ['memberPhone', 'phoneNumber', 'mobile', 'mobileNo', 'phone', 'PhoneNumber']],
  ['customerTypeName', ['customerTypeName', 'CustomerTypeName', 'customer_type_name', 'customerType']],
  [
    'customerSubTypeName',
    [
      'customerSubTypeName',
      'CustomerSubTypeName',
      'customer_sub_type_name',
      'category',
      'memberCategory',
      'memberType',
    ],
  ],
  ['dateOfJoin', ['dateOfJoin', 'DateOfJoin', 'date_of_join', 'memberSince', 'MemberSince']],
  ['age', ['age', 'Age']],
  ['customerTypeid', ['customerTypeid', 'CustomerTypeId', 'customer_type_id', 'customerTypeId']],
  ['customerSubTypeid', ['customerSubTypeid', 'CustomerSubTypeId', 'customer_sub_type_id', 'customerSubTypeId']],
];

export function mapLooseRecordToMemberProfileUi(
  raw: Record<string, unknown> | null | undefined,
  base: MemberProfileForUi = {},
): MemberProfileForUi {
  if (!raw) {
    return { ...base };
  }
  const uiKeys = new Set<string>(MEMBER_PROFILE_STORAGE_KEYS);
  const next: MemberProfileForUi = { ...base };
  for (const [storageKey, jsonKeys] of PROFILE_FIELD_SOURCES) {
    if (!uiKeys.has(storageKey)) continue;
    const v = pickStr(raw, jsonKeys);
    if (v) next[storageKey as MemberProfileStorageKey] = v;
  }
  return next;
}

export function persistMemberProfileFromLooseRecord(
  raw: Record<string, unknown> | null | undefined,
  opts?: { clearPhotoIfMissing?: boolean },
): void {
  if (!raw) {
    return;
  }
  
  const next: MemberProfileForUi = { ...memoryProfile };

  const set = (key: MemberProfileStorageKey, value: unknown) => {
    if (value === null || value === undefined) return;
    const s = `${value}`.trim();
    if (!s || s.toLowerCase() === 'null') return;
    next[key] = s;
  };

  for (const [storageKey, jsonKeys] of PROFILE_FIELD_SOURCES) {
    const v = pickStr(raw, jsonKeys);
    if (v) set(storageKey as MemberProfileStorageKey, v);
  }

  const photo =
    pickStr(raw, ['memberPhoto', 'memberPhotoUrl', 'MemberPhoto', 'photoUrl', 'PhotoUrl', 'member_photo']) ?? '';
  const ok = !!photo && photo.toLowerCase() !== 'null';
  if (ok) {
    next.memberPhoto = photo;
  } else if (opts?.clearPhotoIfMissing) {
    delete next.memberPhoto;
  }
  
  memoryProfile = next;
  
  if (typeof window !== 'undefined') {
    if (next.memberCode) {
      localStorage.setItem('memberCode', next.memberCode);
    }
    if (next.memberName) {
      localStorage.setItem('memberName', next.memberName);
    }
    if (next.memberPhone) {
      localStorage.setItem('memberPhone', next.memberPhone);
    }
    if (next.customerSubTypeName) {
      localStorage.setItem('customerSubTypeName', next.customerSubTypeName);
    }
    if (next.memberPhoto) {
      localStorage.setItem('memberPhoto', next.memberPhoto);
    }
    if (next.dateOfJoin) {
      localStorage.setItem('dateOfJoin', next.dateOfJoin);
    }
    if (next.age) {
      localStorage.setItem('age', next.age);
    }
  }
}

export function persistMemberProfileFromEntity(
  entity: AuthApiResponse['entityResponse'],
  opts?: { clearPhotoIfMissing?: boolean },
): void {
  persistMemberProfileFromLooseRecord(entity as Record<string, unknown>, opts);
}

/** Body row for member-sign / verify (handles alternate envelopes). */
export function coerceSignInEntityRecord(res: AuthApiResponse | null | undefined): Record<string, unknown> | null {
  if (!res) return null;
  const e = res.entityResponse;
  if (e && typeof e === 'object') {
    return { ...(e as unknown as Record<string, unknown>) };
  }
  const data = res.apiResponse?.data as unknown;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

export function isMemberSignResponseSuccess(res: AuthApiResponse | null | undefined): boolean {
  if (!res?.apiResponse) return false;
  const c = res.apiResponse.statusCode as unknown;
  const n = typeof c === 'string' ? Number(c) : c;
  const ok = n === 0 || n === 200;
  if (!ok) return false;
  return coerceSignInEntityRecord(res) != null;
}

@Injectable({
  providedIn: 'root',
})
export class MemberAuth {
  private readonly baseUrl = environment.memberMobileApiUrl;

  constructor(private readonly http: HttpClient) {}

  requestOtp(memberCode: string): Observable<AuthApiResponse> {
    return this.http.post<AuthApiResponse>(`${this.baseUrl}signin/member-sign`, {
      memberCode,
      platForm: 'string',
      versionNumber: 'string',
      fcmToken: 'string',
    });
  }

  verifyOtp(memberCode: string, otp: string): Observable<AuthApiResponse> {
    return this.http.post<AuthApiResponse>(`${this.baseUrl}signin/member-verify-sign-in-otp`, {
      memberCode,
      otp,
      androidVersion: environment.version,
      mobileModel: 'web',
      deviceId: '',
      localIp: '',
      publicIp: '',
      fcmToken: '',
    });
  }
}
